import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, LogOut } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallbackTitle?: string;
    fallbackMessage?: string;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleGoHome = () => {
        window.location.href = '/student/dashboard';
    };

    private handleLogout = () => {
        localStorage.clear();
        window.location.href = '/login';
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-slate-50 dark:bg-charcoal-900 flex items-center justify-center p-6 font-sans">
                    <div className="bg-white dark:bg-charcoal-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-8 sm:p-12 max-w-lg w-full text-center shadow-2xl space-y-6 animate-in fade-in zoom-in-95 duration-200">
                        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto border border-amber-500/20">
                            <AlertTriangle size={32} />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                                {this.props.fallbackTitle || 'Something went wrong'}
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                                {this.props.fallbackMessage || "An unexpected error occurred while loading this view. You can reload the page or return to the dashboard."}
                            </p>
                        </div>

                        {this.state.error?.message && (
                            <div className="p-3 bg-slate-100 dark:bg-charcoal-900 rounded-xl text-xs font-mono text-slate-600 dark:text-slate-400 max-h-24 overflow-y-auto text-left border border-slate-200 dark:border-slate-700/60">
                                {this.state.error.message}
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button
                                onClick={this.handleReload}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-brand-emerald hover:bg-brand-emerald/90 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all cursor-pointer border-none shadow-lg shadow-brand-emerald/20"
                            >
                                <RefreshCw size={15} />
                                <span>Reload Page</span>
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-black uppercase tracking-widest transition-all cursor-pointer border-none"
                            >
                                <Home size={15} />
                                <span>Dashboard</span>
                            </button>
                        </div>

                        <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                            <button
                                onClick={this.handleLogout}
                                className="inline-flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-red-500 transition-colors bg-transparent border-none cursor-pointer"
                            >
                                <LogOut size={13} />
                                <span>Sign out and log in again</span>
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
export default ErrorBoundary;
